import React, { useContext, useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2,
  Box,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import nodeTypesConfig from "../../models/nodeTypesConfig";
import NodeConfigModal from "./menus/NodeConfigModal";
import NodesDynamicForm from "../../forms/NodesDynamicForm";
import AddNodeContext from "../../contexts/AddNodeContext";
import useAutomations from "../../hooks/automationhooks/useAutomations";
import { useParams } from "react-router-dom";
import { getNodeFormFields } from "../../utils/workflow/getNodeFormFields";
import { useThemeContext } from "../../Theme/Theme";

const AddNodeButton = ({ nodesExist }) => {
  const { addNode } = useContext(AddNodeContext);
  const {
    brandSegments,
    campaigns,
    handleGetCampaigns,
    handleGetBrandSegments,
  } = useAutomations();
  const { mode } = useThemeContext();
  const { automationId, brandId } = useParams();
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [configModal, setConfigModal] = useState({
    open: false,
    content: null,
  });

  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const currentBrandId = brandId || localStorage.getItem("brandId");
      if (currentBrandId) {
        setIsLoadingData(true);
        try {
          await Promise.all([
            handleGetCampaigns(currentBrandId),
            handleGetBrandSegments(currentBrandId),
          ]);
        } catch (error) {
          console.error("Error loading initial data:", error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    loadData();
  }, [brandId]);

  const handleOptionsButtonClick = () => {
    setOptionsModalOpen(true);
  };

  // Close the options modal
  const handleOptionsModalClose = () => {
    setOptionsModalOpen(false);
  };

  // Close the configuration modal
  const handleConfigModalClose = () => {
    setConfigModal({ open: false, content: null });
  };

  const handleOptionSelect = (type) => {
    setOptionsModalOpen(false);

    const formFields = getNodeFormFields(
      type,
      nodeTypesConfig,
      brandSegments,
      campaigns
    );
    const config = findNodeTypeConfig(type);

    if (formFields && formFields.length > 0) {
      setConfigModal({
        open: true,
        content: (
          <NodesDynamicForm
            title={`${config.displayName}`}
            formFields={formFields}
            onSubmit={(formData) => {
              addNode(type, formData);
              handleConfigModalClose();
            }}
            onClose={() => {
              handleConfigModalClose();
            }}
          />
        ),
      });
    } else {
      addNode(type);
    }
  };

  const findNodeTypeConfig = (type) => {
    const { triggers } = nodeTypesConfig;
    return triggers.find((node) => node.type === type);
  };

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          ...(nodesExist
            ? {
                // Position under "Go Back" button when nodes exist
                top: "125px",
                left: "10px",
              }
            : {
                // Center in screen when no nodes
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }),
          zIndex: 10,
        }}
      >
        <Button
          onClick={handleOptionsButtonClick}
          variant="contained"
          style={{
            borderRadius: 28,
            minWidth: "auto",
            aspectRatio: "1 / 1",
          }}
          sx={{ backgroundColor: "#0DA3E2" }}
        >
          <AddIcon />
        </Button>
      </Box>

      {/* Options Modal */}
      <Dialog
        open={optionsModalOpen}
        onClose={handleOptionsModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: "600px",
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <DialogTitle
          sx={{ textAlign: "center", bgcolor: "background.default" }}
          color="companyBlue"
        >
          Select Trigger Node
        </DialogTitle>
        <DialogContent
          sx={{
            color: "text.primary",
            bgcolor: "background.paper",
            p: 3,
            pt: "24px !important",
            pb: "24px !important",
            overflow: "hidden",
            minHeight: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isLoadingData ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={60} />
              <DialogTitle>Loading...</DialogTitle>
            </Box>
          ) : (
            <Grid2
              container
              spacing={3}
              justifyContent="center"
              alignItems="center"
              sx={{
                width: "100%",
                m: 0,
                p: "12px",
              }}
            >
              {nodeTypesConfig.triggers.map((node) => (
                <Grid2
                  item
                  xs={12}
                  sm={4}
                  key={node.type}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => handleOptionSelect(node.type)}
                    sx={{
                      width: 180,
                      height: 180,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      p: 2,
                      transition: node.isWip ? "none" : "all 0.2s ease-in-out",
                      cursor: node.isWip ? "not-allowed" : "pointer",
                      opacity: node.isWip ? 0.4 : 1,
                      ...(node.isWip
                        ? { pointerEvents: "none" }
                        : {
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                            },
                          }),
                    }}
                  >
                    <node.icon sx={{ fontSize: "40px", mb: 2 }} />
                    <div>{node.displayName}</div>
                  </Button>
                </Grid2>
              ))}
            </Grid2>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.default" }}>
          <Button
            variant="outlined"
            onClick={handleOptionsModalClose}
            color={"companyBlue"}
            sx={{
              minWidth: 100,
              borderColor: "companyBlue",
              textTransform: "none",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
                borderColor: "rgba(255, 255, 255, 0.5)",
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configuration Modal */}
      {configModal.open && (
        <NodeConfigModal
          open={configModal.open}
          onClose={handleConfigModalClose}
        >
          {configModal.content}
        </NodeConfigModal>
      )}
    </>
  );
};

export default AddNodeButton;
